import React, { FormEvent, useState } from "react";
import { Alert, FormControlLabel, Link, TextField } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";

import { TERMS_OF_USE_URL } from "../../../utils/urlConstants";
import {
  AddressFormFields,
  ContactFields,
  FormSection,
  IntakeFormActions,
  IntakeSubmitButton,
  OptionalLabel,
} from "./PhlebotomySearchPage.styles";
import PhlebotomyCheckbox from "../components/PhlebotomyCheckbox";
import { isValidUsPhoneNumber, US_ZIP_CODE_REGEX } from "./phlebotomyFormUtils";

export interface PhlebotomyIntakeData {
  zip: string;
  wantsTextReminders: boolean;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  estimatedDueDate: string;
}

interface PhlebotomyIntakeFormProps {
  isSubmitting: boolean;
  submissionError: string | null;
  submittedBy: "patient" | "provider";
  onSubmit: (data: PhlebotomyIntakeData) => void;
}

interface ReminderFieldErrors {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

type IntakeTextField = Exclude<
  keyof PhlebotomyIntakeData,
  "wantsTextReminders"
>;

const PhlebotomyIntakeForm: React.FC<PhlebotomyIntakeFormProps> = ({
  isSubmitting,
  submissionError,
  submittedBy,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<PhlebotomyIntakeData>({
    zip: "",
    wantsTextReminders: false,
    firstName: "",
    lastName: "",
    phoneNumber: "",
    estimatedDueDate: "",
  });
  const [zipError, setZipError] = useState<string | null>(null);
  const [reminderFieldErrors, setReminderFieldErrors] =
    useState<ReminderFieldErrors>({});

  const updateField =
    (field: IntakeTextField) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setFormData((current) => ({ ...current, [field]: value }));
      if (field === "zip") setZipError(null);
      if (field !== "zip" && field !== "estimatedDueDate") {
        setReminderFieldErrors((current) => ({
          ...current,
          [field]: undefined,
        }));
      }
    };

  const handleReminderOptInChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const wantsTextReminders = event.target.checked;
    setFormData((current) => ({ ...current, wantsTextReminders }));
    if (!wantsTextReminders) setReminderFieldErrors({});
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmedZip = formData.zip.trim();
    const trimmedPhone = formData.phoneNumber.trim();
    const nextZipError = US_ZIP_CODE_REGEX.test(trimmedZip)
      ? null
      : t("phlebotomyIntake.errors.zipInvalid");
    const nextReminderErrors: ReminderFieldErrors = {};
    if (formData.wantsTextReminders) {
      if (!formData.firstName.trim()) {
        nextReminderErrors.firstName = t(
          "phlebotomyIntake.errors.firstNameRequired",
        );
      }
      if (!formData.lastName.trim()) {
        nextReminderErrors.lastName = t(
          "phlebotomyIntake.errors.lastNameRequired",
        );
      }
      if (!trimmedPhone) {
        nextReminderErrors.phoneNumber = t(
          "phlebotomyIntake.errors.phoneRequired",
        );
      } else if (!isValidUsPhoneNumber(trimmedPhone)) {
        nextReminderErrors.phoneNumber = t(
          "phlebotomyIntake.errors.phoneInvalid",
        );
      }
    }

    setZipError(nextZipError);
    setReminderFieldErrors(nextReminderErrors);
    if (nextZipError || Object.keys(nextReminderErrors).length > 0) return;

    onSubmit({
      ...formData,
      zip: trimmedZip.slice(0, 5),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      phoneNumber: trimmedPhone,
    });
  };

  const reminderOptIn = (
    <FormControlLabel
      control={
        <PhlebotomyCheckbox
          checked={formData.wantsTextReminders}
          onChange={handleReminderOptInChange}
        />
      }
      label={t("phlebotomyIntake.reminders.optIn")}
    />
  );

  return (
    <form onSubmit={handleSubmit} noValidate>
      <AddressFormFields>
        <FormSection>
          <TextField
            fullWidth
            required
            autoComplete="postal-code"
            inputProps={{ inputMode: "numeric" }}
            label={t("phlebotomyIntake.fields.zip")}
            value={formData.zip}
            error={Boolean(zipError)}
            helperText={zipError || t("phlebotomyIntake.fields.zipHelp")}
            onChange={updateField("zip")}
          />
        </FormSection>

        <FormSection>
          <FormSection>
            <ContactFields>
              <TextField
                fullWidth
                required={formData.wantsTextReminders}
                autoComplete="given-name"
                label={t("phlebotomyIntake.fields.firstName")}
                value={formData.firstName}
                error={Boolean(reminderFieldErrors.firstName)}
                helperText={reminderFieldErrors.firstName}
                onChange={updateField("firstName")}
              />
              <TextField
                fullWidth
                required={formData.wantsTextReminders}
                autoComplete="family-name"
                label={t("phlebotomyIntake.fields.lastName")}
                value={formData.lastName}
                error={Boolean(reminderFieldErrors.lastName)}
                helperText={reminderFieldErrors.lastName}
                onChange={updateField("lastName")}
              />
            </ContactFields>
            <TextField
              fullWidth
              required={formData.wantsTextReminders}
              autoComplete="tel"
              inputProps={{ inputMode: "tel" }}
              label={t("phlebotomyIntake.fields.phone")}
              value={formData.phoneNumber}
              error={Boolean(reminderFieldErrors.phoneNumber)}
              helperText={
                reminderFieldErrors.phoneNumber ||
                t("phlebotomyIntake.fields.phoneHelp")
              }
              onChange={updateField("phoneNumber")}
            />
            <TextField
              fullWidth
              type="date"
              label={t("phlebotomyIntake.fields.dueDate")}
              value={formData.estimatedDueDate}
              InputLabelProps={{ shrink: true }}
              onChange={updateField("estimatedDueDate")}
            />
          </FormSection>
          {reminderOptIn}
          <OptionalLabel variant="caption">
            <Trans
              i18nKey={
                submittedBy === "provider"
                  ? "phlebotomyIntake.reminders.providerConsent"
                  : "phlebotomyIntake.reminders.patientConsent"
              }
              components={{
                terms: <Link href={TERMS_OF_USE_URL} target="_blank" />,
                privacy: (
                  <Link
                    href="https://billiontoone.com/privacy/"
                    target="_blank"
                    rel="noreferrer"
                  />
                ),
              }}
            />
          </OptionalLabel>
        </FormSection>

        {submissionError && <Alert severity="error">{submissionError}</Alert>}

        <IntakeFormActions>
          <IntakeSubmitButton
            type="submit"
            variant="unity-primary"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t("phlebotomyIntake.actions.submitting")
              : t(
                  formData.wantsTextReminders
                    ? "phlebotomyIntake.actions.submitWithReminders"
                    : "phlebotomyIntake.actions.submit",
                )}
          </IntakeSubmitButton>
        </IntakeFormActions>
      </AddressFormFields>
    </form>
  );
};

export default PhlebotomyIntakeForm;
